import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useState, useEffect, useRef } from 'react';
import emojiImg from './assets/emoji.png';
import createImg from './assets/creatorImg.png';
import businessImg from './assets/businessImg.png';
import confetti from 'canvas-confetti';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { promotionsAtom } from '../../../store/rodin';
import { usePromotionsFetch, useNewUserPromotionCountdown } from '../../../utils/hooks';

// 在顶部 imports 下添加
const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

// 只添加确实需要映射的常量（有3个或以上不同值的情况）

const emojiScaleMap = [0, 0, 90, 72, 30, 30, 25, 25]
const h2ScaleMap = [0, 1.5, 1.5, 1, 0.8, 0.8, 0.8, 0.8]
const containerWidthMap = [0, 827, 827, 827, 375, 375, 168, 168];
const containerHeightMap = [0, 184, 184, 184, 344, 344, 52, 52];
const emojiTransformMap = [
  'translate(0, 0)',
  'translate(0, 0)',
  'translate(0, 0)',
  'translate(-8px, 0)',
  'translate(26px, 0)',
  'translate(26px, 0)',
  'translate(16px, 0)',
  'translate(16px, 0)'
];

const NewUserPromotion = ({ isOpen, onClose }) => {
  const { handleSubscribePromotion } = usePromotionsFetch();
  const [animationPhase, setAnimationPhase] = useState(0);
  const { timeLeft, startCountdown } = useNewUserPromotionCountdown();
  const confettiRef = useRef(null);
  const confettiOptions = useRef({
    count: 200,
    defaults: {
      origin: { y: 0.7 }
    }
  });
  const [containerPositions, setContainerPositions] = useState({
    initial: '50%',  // 阶段1-3的位置
    medium: null,   // 阶段4-5的位置
    final: null     // 阶段6的位置
  });
  const containerRef = useRef(null);
  const setPromotions = useSetRecoilState(promotionsAtom);

  // 添加一个状态来控制实际的渲染
  const [shouldRender, setShouldRender] = useState(false);

  // 添加图片加载状态
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // 预加载图片的函数
  const preloadImages = async () => {
    const images = [emojiImg, createImg, businessImg];

    try {
      await Promise.all(
        images.map(
          (src) =>
            new Promise((resolve, reject) => {
              const img = new Image();
              img.src = src;
              img.onload = resolve;
              img.onerror = reject;
            })
        )
      );
      setImagesLoaded(true);
    } catch (error) {
      console.error('Error preloading images:', error);
      // 如果加载失败，我们仍然设置为 true，以避免无限等待
      setImagesLoaded(true);
    }
  };

  // 监听 isOpen 变化
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // 重置图片加载状态
      setImagesLoaded(false);
      // 开始预加载图片
      preloadImages();
    }
  }, [isOpen]);

  // 处理动画完成后的清理
  const handleAnimationComplete = () => {
    if (!isOpen) {
      setShouldRender(false);
      onClose();
    }
  };

  function fire(particleRatio, opts) {
    if (!confettiRef.current) return;
    const canvas = confettiRef.current;
    canvas.confetti = canvas.confetti || confetti.create(canvas, { resize: true });
    if (canvas && canvas.confetti) {
      // canvas.confetti({
      //   ...confettiOptions.current.defaults,
      //   ...opts,
      //   particleCount: Math.floor(confettiOptions.current.count * particleRatio)
      // });
      var defaults = {
        scalar: 1,
        spread: 180,  // 扩散范围
        particleCount: 200,  // 粒子数量
        origin: { y: -0.5 }, // 粒子起始位置
        startVelocity: -65 // 粒子起始速度
      };

      canvas.confetti({
        ...defaults,
        colors: ['#ff9a00', '#ff7400', '#ff4d00']
      });
      canvas.confetti({
        ...defaults,
        colors: ['#8d960f', '#be0f10', '#445404']
      });
      canvas.confetti({
        ...defaults,
        colors: ['#f93963', '#a10864', '#ee0b93']
      });
    }
  }


  const startConfetti = () => {
    fire(0.25, {
      spread: 26,
      startVelocity: 55,
    });
    // fire(0.2, {
    //   spread: 60,
    // });
    // fire(0.35, {
    //   spread: 100,
    //   decay: 0.91,
    //   scalar: 0.8
    // });
    // fire(0.1, {
    //   spread: 120,
    //   startVelocity: 25,
    //   decay: 0.92,
    //   scalar: 1.2
    // });
    // fire(0.1, {
    //   spread: 120,
    //   startVelocity: 45,
    // });
  }

  // 修改动画序列逻辑，增加图片加载检查
  useEffect(() => {
    if (isOpen && shouldRender && imagesLoaded) {
      const sequence = async () => {
        await new Promise(resolve => setTimeout(resolve, 100));
        setAnimationPhase(1);
        await new Promise(resolve => setTimeout(resolve, 400));
        setAnimationPhase(2);
        await new Promise(resolve => {
          setTimeout(() => {
            startConfetti();
          }, 450)
          return setTimeout(resolve, 400);
        });
        setAnimationPhase(3);
        await new Promise(resolve => {
          setTimeout(() => {
            const canvas = confettiRef.current;
            canvas.style.opacity = 0;
          }, 450)
          return setTimeout(resolve, 1000);
        });
        setAnimationPhase(4);
        await new Promise(resolve => setTimeout(resolve, 400));
        setAnimationPhase(5);
      };
      sequence();
    } else {
      setAnimationPhase(0);
    }
  }, [isOpen, shouldRender, imagesLoaded]);

  // 在组件挂载时计算位置
  useEffect(() => {
    if (isOpen) {
      // 获取视窗高度
      const viewportHeight = window.innerHeight;

      // 计算各阶段容器在垂直方向上的中心点
      const initialHeight = 184; // 阶段1-3的高度
      const mediumHeight = 344;  // 阶段4-5的高度
      const finalHeight = 52;    // 阶段6的高度

      setContainerPositions({
        initial: (viewportHeight - initialHeight) / 2,
        medium: (viewportHeight - mediumHeight) / 2,
        final: (viewportHeight - mediumHeight) / 2 // 使用medium的位置作为final的起始位置
      });
    }
  }, [isOpen]);

  // 当弹窗打开且动画到达第4阶段时启动倒计时
  useEffect(() => {
    if (isOpen && animationPhase >= 4) {
      startCountdown();
    }
  }, [isOpen, animationPhase, startCountdown]);

  const exitAnimation = async () => {
    setAnimationPhase(6);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setAnimationPhase(7);
    await new Promise(resolve => setTimeout(resolve, 200));

    setPromotions(prev => ({ ...prev, newUser: { ...prev.newUser, show: true } }));
    // 不直接调用 onClose，而是通过设置 isOpen 来触发退出动画
    onClose();
  };

  // 添加调试面板组件
  const DebugPanel = () => (
    <div style={{
      position: 'fixed',
      right: 20,
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'white',
      padding: '10px',
      borderRadius: '8px',
      boxShadow: '0 0 10px rgba(0,0,0,0.2)',
      zIndex: 1001
    }}>
      {[0, 1, 2, 3, 4, 5, 6].map(phase => (
        <button
          key={phase}
          onClick={() => {
            if (phase >= 6) {
              exitAnimation()
            } else {
              setAnimationPhase(phase);
            }
            if (phase >= 3) startConfetti();
          }}
          style={{
            display: 'block',
            margin: '5px 0',
            padding: '5px 10px',
            background: animationPhase === phase ? '#8364DD' : 'white',
            color: animationPhase === phase ? 'white' : 'black',
            border: '1px solid #8364DD',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Phase {phase}
        </button>
      ))}
    </div>
  );

  if (!shouldRender) return null;

  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onAnimationComplete={handleAnimationComplete}
          onClick={(e) => {
            // 只有当点击的是遮罩层本身时才触发退出动画
            if (e.target === e.currentTarget) {
              exitAnimation();
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
          }}
        >
          <div className='absolute-center flex-center confetti-container overflow-hidden w-[817px] h-[184px] z-[1000] pointer-events-none '>
            <canvas ref={confettiRef} className='[transition:opacity_0.2s_ease-in-out]' style={{ width: '817px', height: '600px' }} />
          </div>
          <motion.div
            onClick={(e) => e.stopPropagation()} // 阻止内容区域的点击事件冒泡
            ref={containerRef}
            initial={{ width: 0, height: 0, top: containerPositions.initial }}
            animate={{
              width: containerWidthMap[animationPhase],
              height: containerHeightMap[animationPhase],
              transform: animationPhase >= 7 ? 'translateX(-50%) scale(0)' : 'translateX(-50%) scale(1)',
              top: containerPositions[
                animationPhase >= 6 ? 'final' : (animationPhase >= 4 ? 'medium' : 'initial')
              ],
              background: animationPhase >= 4
                ? 'linear-gradient(142.28deg, #FFFFFF 42.3%, #EFEBF9 88.47%)'
                : 'rgba(131, 100, 221, 1)',
              borderRadius: animationPhase >= 4 ? 25 : 42,
              border: animationPhase >= 4 ? 'none' : '2px solid rgba(255, 255, 255, 0.27)',
              boxShadow: '0px 0px 44px 0px rgba(106, 51, 222, 0.48)',
            }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
              background: { duration: 0.8 },
              width: { delay: animationPhase >= 6 ? 0.1 : 0 },
              height: { delay: animationPhase >= 6 ? 0.1 : 0 },
            }}
            style={{
              position: 'fixed',
              left: '50%',
              transform: 'translateX(-50%)',
              overflow: 'hidden'
            }}
          >
            {/* 添加关闭按钮 - 放在容器内部的最前面 */}
            {animationPhase >= 4 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{
                  opacity: animationPhase >= 6 ? 0 : 1,
                  scale: 1
                }}
                transition={{ duration: 0.2 }}
                onClick={exitAnimation}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  width: '24px',
                  height: '24px',
                  cursor: 'pointer',
                  zIndex: 200,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  userSelect: 'none'
                }}
                whileHover={{ scale: 1.1 }}
              >
                <motion.span
                  style={{
                    position: 'absolute',
                    width: '16px',
                    height: '2px',
                    background: 'rgba(131, 100, 221, 0.8)',
                    borderRadius: '2px',
                    transform: 'rotate(45deg)',
                    transition: 'background-color 0.2s ease'
                  }}
                  whileHover={{
                    background: 'rgba(131, 100, 221, 1)'
                  }}
                />
                <motion.span
                  style={{
                    position: 'absolute',
                    width: '16px',
                    height: '2px',
                    background: 'rgba(131, 100, 221, 0.8)',
                    borderRadius: '2px',
                    transform: 'rotate(-45deg)',
                    transition: 'background-color 0.2s ease'
                  }}
                  whileHover={{
                    background: 'rgba(131, 100, 221, 1)'
                  }}
                />
              </motion.div>
            )}
            <motion.div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                left: animationPhase < 3 ? '50%' : '0',
                position: 'absolute',
                top: animationPhase >= 6 ? '8px' : (animationPhase >= 4 ? '16px' : '50%'),
                transform: animationPhase >= 4 ? 'translateY(0)' : animationPhase < 3 ? 'translate(-80px ,-50%)' : 'translate(0 ,-50%)',
                transition: 'all 0.3s ease-in-out'
              }}
            >
              <motion.img
                src={emojiImg}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: animationPhase >= 2 ? 1 : 0,
                  transform: emojiTransformMap[animationPhase],
                  width: emojiScaleMap[animationPhase],
                  height: emojiScaleMap[animationPhase]
                }}
                transition={{
                  duration: 0.3,
                  opacity: { duration: 0.2 },
                  transform: { duration: animationPhase >= 3 ? 0.3 : 0.6 },
                  width: animationPhase === 2 ? {
                    duration: 0.6,
                    times: [0, 0.6, 1],
                    keyframes: [0, 110, 90]  // 从0到110再到90
                  } : {
                    duration: 0.3
                  },
                  height: animationPhase === 2 ? {
                    duration: 0.6,
                    times: [0, 0.6, 1],
                    keyframes: [0, 110, 90]  // 从0到110再到90
                  } : {
                    duration: 0.3
                  }
                }}
              />
              <motion.div
                initial={{ opacity: 0 }}
                animate={{
                  opacity: animationPhase >= 3 ? 1 : 0,
                }}
                transition={{ duration: 0.3 }}
                style={{ overflow: 'hidden' }}
              >
                <motion.h2
                  initial={{ opacity: 0, x: 100 }}
                  animate={{
                    opacity: animationPhase >= 6 ? 0 : (animationPhase >= 3 ? 1 : 0),
                    x: animationPhase >= 6 ? -100 : (animationPhase >= 3 ? 0 : '-200%'),
                    scale: h2ScaleMap[animationPhase] ?? 1,
                    y: 0,
                    color: animationPhase >= 4 ? 'rgba(131,100,221,1)' : '#fff',
                    fontSize: animationPhase >= 4 ? '27px' : '58px',
                    fontWeight: '800'
                  }}
                  transition={{ duration: animationPhase >= 3 ? 0.3 : 0.6 }}
                >
                  CONGRATULATIONS!
                </motion.h2>
              </motion.div>

            </motion.div>

            <motion.div
              initial={{
                opacity: 0,
                transform: 'translate(30%,0)',
                pointerEvents: 'none'
              }}
              animate={{
                opacity: animationPhase >= 6 ? 1 : 0,
                transform: animationPhase >= 6 ? 'translate(0,0)' : 'translate(30%,0)',
              }}
              transition={{ duration: 0.3, delay: 0.15 }}
              style={{
                zIndex: 100,
                position: 'absolute',
                right: 12,
                top: '14px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '14px',
                color: '#fff',
              }}
            >
              {Object.entries(timeLeft).map(([unit, value], index) => (
                <motion.div
                  key={unit}
                  style={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <motion.div
                    style={{
                      background: 'rgba(131,100,221,1)',
                      padding: '2px 4px',
                      borderRadius: '7px',
                    }}
                  >
                    <TimeDigit value={value} />
                  </motion.div>
                  {index < 2 && <span style={{ margin: '0 2px', color: 'rgba(154,128,227,1)' }}>:</span>}
                </motion.div>
              ))}
            </motion.div>

            {animationPhase >= 4 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{
                  opacity: animationPhase >= 6 ? 0 : 1,
                  y: animationPhase >= 6 ? -20 : 0
                }}
                transition={{
                  duration: 0.2
                }}
                style={{
                  textAlign: 'center',
                  width: '100%',
                  padding: '0 16px',
                  position: 'absolute',
                  top: '60px',
                  left: 0,
                  right: 0
                }}
              >
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  style={{
                    fontSize: '16px',
                    color: 'rgba(131,100,221,1)',
                    marginBottom: '12px',
                    lineHeight: '1'
                  }}
                >
                  You&apos;ve been randomly selected as a lucky user<br />
                  to unlock an exclusive discount!
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{
                    boxShadow: '0px 0px 0px 1px #4a00e0',
                    scale: 1.01
                  }}
                  transition={{
                    default: { delay: 0.5 },
                    boxShadow: { delay: 0, duration: 0.15 },
                    scale: { delay: 0, duration: 0.15 }
                  }}
                  onClick={handleSubscribePromotion.bind(null, 0)}
                  style={{
                    width: '100%',
                    margin: '12px auto',
                    borderRadius: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <motion.img
                    src={createImg}
                    style={{
                      height: '100%',
                      width: '100%',
                      display: 'block',
                    }}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{
                    boxShadow: '0px 0px 0px 1px rgba(191,158,114,1)',
                    scale: 1.01
                  }}
                  onClick={handleSubscribePromotion.bind(null, 1)}
                  transition={{
                    default: { delay: 0.6 },
                    boxShadow: { delay: 0, duration: 0.15 },
                    scale: { delay: 0, duration: 0.15 }
                  }}
                  style={{
                    width: '100%',
                    margin: '12px auto',
                    borderRadius: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <motion.img
                    src={businessImg}
                    style={{
                      width: '100%',
                      display: 'block',
                    }}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: 1,
                    transform: 'translate(-50%, calc(-50% - 25px))'
                  }}
                  transition={{ delay: 0.55 }}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    margin: '16px 0',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: 'rgba(131,100,221,1)',
                    position: 'absolute',
                    bottom: '48px',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    background: 'rgba(255,255,255,0.55)',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0px 2px 10px 0px rgba(0, 0, 0, 0.15)',
                    borderRadius: '16px',
                    padding: '6px 12px',
                    zIndex: 1,
                    width: 'max-content',
                  }}
                >
                  Only&nbsp;
                  {Object.entries(timeLeft).map(([unit, value], index) => (
                    <motion.div
                      key={unit}
                      style={{
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <motion.div
                        style={{
                          background: 'rgba(0,0,0,0.18)',
                          padding: '2px 4px',
                          borderRadius: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          color: '#fff'
                        }}
                      > <TimeDigit value={value} />
                      </motion.div>
                      {index < 2 && <span style={{ margin: '0 2px' }}>:</span>}
                    </motion.div>
                  ))}
                  &nbsp;to save
                </motion.div>
              </motion.div>
            )}
          </motion.div>
          {/* {process.env.NODE_ENV === 'development' && <DebugPanel />} */}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const TimeDigit = ({ value }) => {
  const prevValue = usePrevious(value);

  return (
    <div
      data-countdown-user
      style={{
        position: 'relative',
        height: '21px',
        width: '20px',
        overflow: 'hidden',
        textAlign: 'center'
      }}>
      <AnimatePresence mode="popLayout">
        {prevValue !== value && (
          <motion.div
            key={`prev-${prevValue}`}
            initial={{ y: 0 }}
            animate={{ y: 21 }}
            exit={{ y: 21 }}
            transition={{
              duration: 0.2,
              ease: 'easeIn'
            }}
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {String(prevValue).padStart(2, '0')}
          </motion.div>
        )}
        <motion.div
          key={`current-${value}`}
          initial={{ y: -21 }}
          animate={{ y: 0 }}
          transition={{
            duration: 0.2,
            ease: 'easeOut'
          }}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {String(value).padStart(2, '0')}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};


// Header 的 button组件
const NewUserPromotionButton = ({ children, onClick }) => {

  const { handleSubscribePromotion } = usePromotionsFetch();
  const { timeLeft, startCountdown } = useNewUserPromotionCountdown();
  const [isHovered, setIsHovered] = useState(false);
  const promotions = useRecoilValue(promotionsAtom);
  if (!promotions.newUser.show) return null;

  // useEffect(() => {
  //   startCountdown()
  // }, [])

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        style={{
          width: '114px',
          height: '40px',
          background: '#fff',
          borderRadius: '20px',
          position: 'relative',
          cursor: 'pointer',
        }}
      >
        <motion.img
          src={emojiImg}
          style={{
            width: '20px',
            height: '20px',
            margin: '10px 0 0 12px',
          }}
        />
        {/* <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: isHovered ? 0 : 1 }}
          transition={{ duration: 0.2 }}
          style={{
            zIndex: 100,
            position: 'absolute',
            right: '8px',
            top: '8px',
            display: 'flex',
            alignItems: 'center',
            fontSize: '14px',
            color: '#fff',
          }}
        >
          {Object.entries(timeLeft).map(([unit, value], index) => (
            <motion.div
              key={unit}
              style={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <motion.div
                style={{
                  background: 'rgba(131,100,221,1)',
                  padding: '1px 2px',
                  borderRadius: '7px',
                }}
              >
                <TimeDigit value={value} />
              </motion.div>
              {index < 2 && <span style={{ margin: '0 2px', color: 'rgba(154,128,227,1)' }}>:</span>}
            </motion.div>
          ))}
        </motion.div> */}

        {/* DISCOUNT 文字 */}
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: isHovered ? 1 : 1 }}
          transition={{ duration: 0.2 }}
          style={{
            position: 'absolute',
            width: 'max-content',
            right: '11px',
            top: '9px',
            fontSize: '14px',
            fontWeight: '800',
            color: 'rgba(131,100,221,1)',
          }}
        >
          50% OFF
        </motion.div>

        {/* 容器 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{
            opacity: isHovered ? 1 : 0,
            transform: isHovered ? 'translate(-50%,100%) scale(1)' : 'translate(-50%,100%) scale(0)'
          }}
          style={{
            transformOrigin: 'center top',
            position: 'absolute',
            bottom: '0',
            left: '50%',
            width: '375px',
            paddingTop: '12px',
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <div className="flex flex-col gap-2 bg-white p-[16px] rounded-[16px] will-change-transform">
            {/* 第一行：Congratulations + emoji */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '100%',
              }}
            >
              <img
                src={emojiImg}
                style={{
                  width: '30px',
                  height: '30px',
                }}
              />
              <h2
                style={{
                  fontSize: '25px',
                  color: 'rgba(131,100,221,1)',
                  fontWeight: '800'
                }}
              >
                CONGRATULATIONS!
              </h2>

            </div>
            {/* 第二行：You're selected text */}
            <p
              style={{
                fontSize: '16px',
                color: 'rgba(131,100,221,1)',
                lineHeight: '1'
              }}
            >
              You&apos;ve been randomly selected as a lucky user<br />
              to unlock an exclusive discount!
            </p>
            <motion.div
              whileHover={{
                boxShadow: '0px 0px 0px 1px #4a00e0',
                scale: 1.01
              }}
              onClick={handleSubscribePromotion.bind(null, 0)}
              transition={{
                boxShadow: { delay: 0, duration: 0.15 },
                scale: { delay: 0, duration: 0.15 }
              }}
              style={{
                width: '100%',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              <img
                src={createImg}
                style={{
                  height: '100%',
                  width: '100%',
                  display: 'block',
                }}
              />
            </motion.div>
            <motion.div
              whileHover={{
                boxShadow: '0px 0px 0px 1px rgba(191,158,114,1)',
                scale: 1.01
              }}
              onClick={handleSubscribePromotion.bind(null, 1)}
              transition={{
                boxShadow: { delay: 0, duration: 0.15 },
                scale: { delay: 0, duration: 0.15 }
              }}
              style={{
                width: '100%',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              <img
                src={businessImg}
                style={{
                  width: '100%',
                  display: 'block',
                }}
              />
            </motion.div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '16px 0',
                fontSize: '12px',
                fontWeight: 'bold',
                color: 'rgba(131,100,221,1)',
                position: 'absolute',
                bottom: '48px',
                left: '50%',
                transform: 'translate(-50%, calc(-50% - 25px))',
                background: 'rgba(255,255,255,0.55)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0px 2px 10px 0px rgba(0, 0, 0, 0.15)',
                borderRadius: '16px',
                padding: '6px 12px',
                zIndex: 1,
                width: 'max-content',
              }}
            >
              Only&nbsp;
              {Object.entries(timeLeft).map(([unit, value], index) => (
                <div
                  key={unit}
                  style={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <div
                    style={{
                      background: 'rgba(0,0,0,0.18)',
                      padding: '2px 4px',
                      borderRadius: '4px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      color: '#fff'
                    }}
                  > <TimeDigit value={value} />
                  </div>
                  {index < 2 && <span style={{ margin: '0 2px' }}>:</span>}
                </div>
              ))}
              &nbsp;to save
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NewUserPromotion;
export { NewUserPromotionButton };